import { createContext, useContext, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { Navbar, scrollToSection } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { ChiSiamo } from "@/components/ChiSiamo";
import { Team } from "@/components/Team";
import { Servizi } from "@/components/Servizi";
import { Perche } from "@/components/Perche";
import { Portfolio } from "@/components/Portfolio";
import { Contatti } from "@/components/Contatti";
import { Footer } from "@/components/Footer";
import { LogoMark } from "@/components/Logo";
import ServicePage from "@/components/ServicePage";
import GuidePage from "@/components/GuidePage";
import ArticlePage from "@/components/ArticlePage";
import AdminPage from "@/components/AdminPage";
import { LanguageProvider } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext({ user: undefined, setUser: () => {}, logout: async () => {} });
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = verifica in corso, null = non autenticato

  useEffect(() => {
    // CRITICAL: se stiamo tornando dal callback OAuth, salta il controllo /me.
    // AuthCallback scambia prima il session_id e stabilisce la sessione.
    if (window.location.hash?.includes("session_id=")) return;
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => setUser(u))
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } finally {
      setUser(null);
    }
  };

  return <AuthContext.Provider value={{ user, setUser, logout }}>{children}</AuthContext.Provider>;
}

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    fetch(`${API}/auth/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("auth failed"))))
      .then((u) => {
        setUser(u);
        navigate("/", { replace: true });
      })
      .catch(() => navigate("/", { replace: true }));
  }, [location.hash, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#070D18] flex flex-col items-center justify-center gap-5" data-testid="auth-callback-screen">
      <LogoMark className="w-14 h-14 animate-pulse" />
      <p className="text-sm tracking-[0.25em] uppercase text-[#D4AF37]">Accesso in corso…</p>
    </div>
  );
}

function Site() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash && !location.hash.includes("session_id=")) {
      const t = setTimeout(() => scrollToSection(location.hash), 400);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <div className="grain bg-[#070D18] min-h-screen" data-testid="adriatica-site">
      <Navbar />
      <main>
        <Hero />
        <Marquee />
        <ChiSiamo />
        <Team />
        <Servizi />
        <Perche />
        <Portfolio />
        <Contatti />
      </main>
      <Footer />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Site />} />
      <Route path="/servizi/:slug" element={<ServicePage />} />
      <Route path="/guide" element={<GuidePage />} />
      <Route path="/guide/:slug" element={<ArticlePage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="*" element={<Site />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{ style: { background: "#0E1726", border: "1px solid rgba(212,175,55,0.35)", color: "#E2E8F0" } }}
          />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
